import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../services/api';
import { OnboardingStatus } from '@dsgvo-buddy/types';
import { ONBOARDING_STEP_TEXTS, ONBOARDING_STEP_ORDER } from '../constants/onboarding';
import {
  Card,
  CardHeader,
  Text,
  Button,
  FlexBox,
  Icon,
  ProgressIndicator
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

export default function OnboardingWidget() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);

  useEffect(() => {
    onboardingApi.getStatus().then(res => {
      if (res.success && res.data) setStatus(res.data);
    });
  }, []);

  if (!status) return null;

  const stepsMap = new Map(status.steps.map(s => [s.stepKey, s]));

  // Find next open step
  const nextStepKey = ONBOARDING_STEP_ORDER.find(key => {
    const s = stepsMap.get(key);
    return s && !s.completed && !s.dismissed;
  });

  // Complete state
  if (status.overallProgress === 100) {
    return (
      <Card
        header={
          <CardHeader
            titleText="Datenschutz-Kompass"
            subtitleText="Alle Grundlagen dokumentiert"
            avatar={<Icon name="accept" style={{ color: 'var(--sapPositiveColor)' }} />}
            action={
              <Button design="Transparent" onClick={() => navigate('/onboarding')}>
                Details anzeigen
              </Button>
            }
          />
        }
      >
        <div style={{ padding: '0.75rem 1.25rem 1.25rem' }}>
          <FlexBox alignItems="Center" style={{ gap: '0.75rem' }}>
            <Icon name="accept" style={{ color: 'var(--sapPositiveColor)', fontSize: '1.25rem' }} />
            <Text style={{ color: 'var(--sapPositiveColor)', fontWeight: '600' }}>
              Ihr DSGVO-Konzept steht auf einem soliden Fundament.
            </Text>
          </FlexBox>
        </div>
      </Card>
    );
  }

  const nextText = nextStepKey ? ONBOARDING_STEP_TEXTS[nextStepKey] : null;
  const nextStep = nextStepKey ? stepsMap.get(nextStepKey) : null;

  return (
    <Card
      header={
        <CardHeader
          titleText="Datenschutz-Kompass"
          subtitleText={`${status.steps.filter(s => s.completed).length} von ${status.steps.length} Schritten erledigt`}
          avatar={<Icon name="compass" />}
          action={
            <Button design="Transparent" onClick={() => navigate('/onboarding')}>
              Alle Schritte anzeigen
            </Button>
          }
        />
      }
    >
      <div style={{ padding: '0 1.25rem 1.25rem' }}>
        <ProgressIndicator
          value={status.overallProgress}
          displayValue={`${status.overallProgress}%`}
          
          style={{ width: '100%', marginBottom: '1rem' }}
        />

        {nextText && nextStep && (
          <div style={{
            background: 'var(--sapInformativeBackground, #e3f2fd)',
            borderRadius: '0.5rem',
            padding: '1rem 1.25rem',
          }}>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Icon name={nextText.icon} style={{ color: 'var(--sapInformativeColor)' }} />
              <Text style={{ fontWeight: '600', fontSize: '0.9375rem' }}>
                Nächster Schritt: {nextText.title}
              </Text>
            </FlexBox>
            {nextStep.progress.target > 1 && (
              <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.5rem' }}>
                Fortschritt: {nextStep.progress.current} von {nextStep.progress.target}
              </Text>
            )}
            <Button
              design="Emphasized"
              icon={nextText.icon}
              onClick={() => navigate(nextText.ctaRoute)}
              style={{ marginTop: '0.25rem' }}
            >
              {nextText.ctaLabel}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
